import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Sparkles } from "lucide-react";
import merchantApi from "../../lib/merchantApi";
import MerchantAuthLayout from "./MerchantAuthLayout";

/**
 * Brief 3.4 §2.7 — magic-link onboarding landing page.
 *
 * Lands at /onboard/:token. Calls /api/onboarding/prefill to verify the
 * token and resolve the merchant's pre-filled fields, then redirects to
 * /signup with the email + legal name + token attached as query params.
 *
 * Shows clear, friendly copy on every failure mode the API can return:
 *   • 404 — token unknown ("link wasn't recognised")
 *   • 410 — used / expired / superseded ("link no longer valid")
 *   • 5xx — generic transient error
 *
 * The token never appears in the URL we forward to /signup as a literal
 * — we keep it in the query so the actual single-use consumption happens
 * at signup-submit time, not at landing-page-load time. (Loading the
 * landing page should be idempotent; we don't want a Slack URL preview
 * to consume the token.)
 */
export default function OnboardLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState({ kind: "error", code: 404, message: "Missing onboarding token." });
        return;
      }
      try {
        const { data } = await merchantApi.get("/onboarding/prefill", {
          params: { token },
        });
        if (cancelled) return;
        // Forward to signup with prefill query params. Replace history so
        // the user can't navigate back into the landing page (which is
        // the expected single-step shape — they came from a link, they
        // should land on signup).
        const params = new URLSearchParams();
        params.set("onboarding_token", token);
        if (data.primary_contact_email) params.set("email", data.primary_contact_email);
        if (data.legal_name) params.set("legal_name", data.legal_name);
        navigate(`/signup?${params.toString()}`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status || 0;
        const detail = err?.response?.data?.detail;
        // Backend now returns a structured {reason, message} payload so
        // we can switch on `reason` instead of string-matching the human
        // message. Older shapes (plain string detail) still flow through.
        const reason = (detail && typeof detail === "object" && detail.reason) || null;
        const message = (detail && typeof detail === "object" ? detail.message : detail) || "We couldn't load this onboarding link.";
        setState({ kind: "error", code, reason, message });
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  if (state.kind === "loading") {
    return (
      <MerchantAuthLayout
        eyebrow="Welcome to Neptune"
        title="Setting things up…"
        subtitle="One moment while we confirm your onboarding link."
        testId="onboard-landing-loading"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-3 text-[13px] text-zinc-400"
          data-testid="onboard-landing-spinner"
        >
          <span className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse" />
          Verifying your link…
        </motion.div>
      </MerchantAuthLayout>
    );
  }

  // Distinct copy per failure mode so the merchant immediately knows
  // what to do next. Three distinguishable states (Brief 3.4 §2.7
  // verification round): not_found / expired / used (+superseded ≈ used).
  const reason = state.reason;
  let headline, detail, primary;
  if (state.code === 410 && reason === "used") {
    headline = "Link already used";
    detail = "This onboarding link has already been used. If that was you, sign in. If you've lost access, reset your password.";
    primary = { to: "/login", label: "Sign in", testId: "onboard-landing-go-login" };
  } else if (state.code === 410 && reason === "superseded") {
    headline = "A newer link is available";
    detail = "This onboarding link was replaced by a newer one. Please use the most recent link your Neptune contact sent you, or ask them to resend it.";
    primary = { to: "/contact", label: "Contact Neptune", testId: "onboard-landing-contact-cta" };
  } else if (state.code === 410) {
    // expired (or any other 410 we didn't anticipate)
    headline = "This link has expired";
    detail = "Onboarding links are valid for 7 days. Please contact your Neptune team and they'll send you a fresh one in seconds.";
    primary = { to: "/contact", label: "Contact Neptune", testId: "onboard-landing-contact-cta" };
  } else if (state.code === 404) {
    headline = "Link not recognised";
    detail = "We couldn't find this onboarding link. Please double-check the URL, or ask your Neptune contact to send it again.";
    primary = { to: "/contact", label: "Contact Neptune", testId: "onboard-landing-contact-cta" };
  } else {
    headline = "Something went wrong";
    detail = state.message;
    primary = { to: "/contact", label: "Contact Neptune", testId: "onboard-landing-contact-cta" };
  }

  return (
    <MerchantAuthLayout
      eyebrow="Onboarding link"
      title={headline}
      subtitle={null}
      testId="onboard-landing-error"
    >
      <div
        className="rounded-2xl border border-amber-400/15 bg-amber-400/[0.04] px-5 py-4 flex items-start gap-3"
        data-testid="onboard-landing-error-banner"
        data-reason={reason || ""}
      >
        <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-300 shrink-0" />
        <div className="text-[13px] text-zinc-200 leading-relaxed">{detail}</div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to={primary.to}
          data-testid={primary.testId}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-5 py-2.5 text-[13px] font-medium"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {primary.label}
        </Link>
        {reason === "used" ? (
          <Link
            to="/reset-password"
            data-testid="onboard-landing-reset-cta"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.03] hover:bg-white/[0.06] text-white transition-colors px-5 py-2.5 text-[13px]"
          >
            Reset password
          </Link>
        ) : (
          <Link
            to="/login"
            data-testid="onboard-landing-go-login"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.03] hover:bg-white/[0.06] text-white transition-colors px-5 py-2.5 text-[13px]"
          >
            I already have an account
          </Link>
        )}
      </div>
    </MerchantAuthLayout>
  );
}
